import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { brokers, submissions, accounts } from '../services/api.js';
import { useAuth } from '../auth/AuthContext.jsx';
import './BrokerDetail.css';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatCurrency(val) {
  if (val == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val);
}

const TABS = ['Overview', 'Contacts', 'Activity', 'Submissions & Accounts'];

const ACTIVITY_TYPES = ['Meeting', 'Call', 'Email', 'Note'];

const ACTIVITY_ICONS = {
  Meeting: '\u{1F91D}',
  Call: '\u{1F4DE}',
  Email: '\u{1F4E7}',
  Note: '\u{1F4DD}',
  System: '\u{2699}\u{FE0F}',
};

export function BrokerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [broker, setBroker] = useState(null);
  const [allBrokers, setAllBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('Overview');

  // Edit broker state
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);

  // Contacts state (API-backed)
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [contactSaving, setContactSaving] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    title: '',
    primary: false,
  });
  const [editingContactId, setEditingContactId] = useState(null);
  const [editContactForm, setEditContactForm] = useState({});
  const [editContactSaving, setEditContactSaving] = useState(false);
  const [deletingContactId, setDeletingContactId] = useState(null);

  // Activity state (API-backed)
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [showLogActivity, setShowLogActivity] = useState(false);
  const [activitySaving, setActivitySaving] = useState(false);
  const [activityForm, setActivityForm] = useState({
    type: 'Note',
    subject: '',
    description: '',
  });

  // Submissions & Accounts state
  const [brokerSubmissions, setBrokerSubmissions] = useState([]);
  const [brokerAccounts, setBrokerAccounts] = useState([]);
  const [subsLoading, setSubsLoading] = useState(false);
  const [subsError, setSubsError] = useState(null);
  const [acctsError, setAcctsError] = useState(null);

  // Sub-agents: add office form
  const [showAddOffice, setShowAddOffice] = useState(false);
  const [officeForm, setOfficeForm] = useState({
    Name: '',
    SubCode: '',
    City: '',
    State: '',
    ContactEmail: '',
  });
  const [officeSaving, setOfficeSaving] = useState(false);

  const loadContacts = useCallback(async () => {
    setContactsLoading(true);
    try {
      const res = await brokers.listContacts(id);
      const raw = res.data || res;
      const list = raw.data || raw.items || raw || [];
      setContacts(Array.isArray(list) ? list : []);
    } catch {
      // If contacts endpoint fails, start with empty list
      setContacts([]);
    } finally {
      setContactsLoading(false);
    }
  }, [id]);

  const loadActivities = useCallback(async () => {
    setActivitiesLoading(true);
    try {
      const res = await brokers.listActivities(id);
      const raw = res.data || res;
      const list = raw.data || raw.items || raw || [];
      setActivities(Array.isArray(list) ? list : []);
    } catch {
      // If activities endpoint fails, start with empty list
      setActivities([]);
    } finally {
      setActivitiesLoading(false);
    }
  }, [id]);

  const loadBroker = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [brokerRes, listRes] = await Promise.allSettled([
        brokers.get(id),
        brokers.list(),
      ]);

      if (brokerRes.status === 'fulfilled') {
        const data = brokerRes.value.data || brokerRes.value;
        setBroker(data);
      } else {
        setError(brokerRes.reason?.message || 'Failed to load broker');
      }

      if (listRes.status === 'fulfilled') {
        const list = listRes.value.data || listRes.value;
        setAllBrokers(Array.isArray(list) ? list : []);
      }

      // Load contacts and activities from API
      loadContacts();
      loadActivities();
    } catch (err) {
      setError(err.message || 'Failed to load broker');
    } finally {
      setLoading(false);
    }
  }, [id, loadContacts, loadActivities]);

  useEffect(() => {
    loadBroker();
  }, [loadBroker]);

  // Load submissions & accounts for stats and when tab is activated
  useEffect(() => {
    // Always load on mount for quick stats, and reload when tab is activated
    let cancelled = false;

    async function loadSubsAccts() {
      setSubsLoading(true);
      setSubsError(null);
      setAcctsError(null);

      const [subsRes, acctsRes] = await Promise.allSettled([
        submissions.list({ brokerId: id }),
        accounts.list({ brokerId: id }),
      ]);

      if (cancelled) return;

      if (subsRes.status === 'fulfilled') {
        const data = subsRes.value.data || subsRes.value;
        setBrokerSubmissions(Array.isArray(data) ? data : []);
      } else {
        setSubsError(subsRes.reason?.message || 'Could not load submissions');
      }

      if (acctsRes.status === 'fulfilled') {
        const data = acctsRes.value.data || acctsRes.value;
        setBrokerAccounts(Array.isArray(data) ? data : []);
      } else {
        setAcctsError(acctsRes.reason?.message || 'Could not load accounts');
      }

      setSubsLoading(false);
    }

    loadSubsAccts();
    return () => {
      cancelled = true;
    };
  }, [activeTab, id]);

  // Edit handlers
  const startEditing = () => {
    setEditData({ ...broker });
    setEditing(true);
    setEditError(null);
  };

  const handleEditChange = (field, value) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  const saveEdit = async () => {
    setEditSaving(true);
    setEditError(null);
    try {
      const payload = {
        name: editData.Name || editData.name,
        contactEmail: editData.ContactEmail || editData.contactEmail,
        brokerType: editData.Type || editData.brokerType,
        phone: editData.Phone || editData.phone,
        address: editData.Address || editData.address,
        city: editData.City || editData.city,
        state: editData.State || editData.state,
        zipCode: editData.ZipCode || editData.zipCode,
        website: editData.Website || editData.website,
        notes: editData.Notes || editData.notes,
        masterCode: editData.MasterCode || editData.masterCode,
      };
      // Remove undefined/empty values
      Object.keys(payload).forEach(k => { if (!payload[k]) delete payload[k]; });
      await brokers.update(id, payload);
      // Refresh broker data from server
      const result = await brokers.get(id);
      const data = result.data || result;
      setBroker(data);
      setEditing(false);
    } catch (err) {
      setEditError(err.message || 'Failed to update broker');
    } finally {
      setEditSaving(false);
    }
  };

  // Contact handlers
  const handleAddContact = async (e) => {
    e.preventDefault();
    setContactSaving(true);
    try {
      await brokers.addContact(id, contactForm);
      setContactForm({ name: '', email: '', phone: '', title: '', primary: false });
      setShowAddContact(false);
      await loadContacts();
    } catch (err) {
      console.error('Failed to add contact:', err);
    } finally {
      setContactSaving(false);
    }
  };

  const startEditContact = (contact) => {
    const cId = contact.ContactID || contact.contactId || contact.id;
    setEditingContactId(cId);
    setEditContactForm({
      name: contact.Name || contact.name || '',
      email: contact.Email || contact.email || '',
      phone: contact.Phone || contact.phone || '',
      title: contact.Title || contact.title || '',
      primary: contact.Primary ?? contact.primary ?? false,
    });
  };

  const handleUpdateContact = async (e) => {
    e.preventDefault();
    setEditContactSaving(true);
    try {
      await brokers.updateContact(id, editingContactId, editContactForm);
      setEditingContactId(null);
      setEditContactForm({});
      await loadContacts();
    } catch (err) {
      console.error('Failed to update contact:', err);
    } finally {
      setEditContactSaving(false);
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    setDeletingContactId(contactId);
    try {
      await brokers.deleteContact(id, contactId);
      await loadContacts();
    } catch (err) {
      console.error('Failed to delete contact:', err);
    } finally {
      setDeletingContactId(null);
    }
  };

  // Activity handlers
  const handleLogActivity = async (e) => {
    e.preventDefault();
    setActivitySaving(true);
    try {
      await brokers.logActivity(id, {
        type: activityForm.type,
        subject: activityForm.subject,
        description: activityForm.description,
      });
      setActivityForm({ type: 'Note', subject: '', description: '' });
      setShowLogActivity(false);
      await loadActivities();
    } catch (err) {
      console.error('Failed to log activity:', err);
    } finally {
      setActivitySaving(false);
    }
  };

  // Add office handler
  const handleAddOffice = async (e) => {
    e.preventDefault();
    setOfficeSaving(true);
    try {
      await brokers.create({
        name: officeForm.Name,
        brokerType: broker?.Type || broker?.brokerType || 'wholesale',
        contactEmail: officeForm.ContactEmail,
        parentBrokerId: id,
        subCode: officeForm.SubCode || undefined,
        city: officeForm.City || undefined,
        state: officeForm.State || undefined,
      });
      setShowAddOffice(false);
      setOfficeForm({ Name: '', SubCode: '', City: '', State: '', ContactEmail: '' });
      // Refresh broker list for sub-agents
      const listRes = await brokers.list();
      const list = listRes.data || listRes;
      setAllBrokers(Array.isArray(list) ? list : []);
    } catch {
      // Silently handle - the add office is best-effort
    } finally {
      setOfficeSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bkd-loading">
        <div className="spinner" />
        <p>Loading broker...</p>
      </div>
    );
  }

  if (error && !broker) {
    return (
      <div className="bkd-error">
        <h2>Error</h2>
        <p>{error}</p>
        <button className="btn btn--primary" onClick={loadBroker}>
          Retry
        </button>
      </div>
    );
  }

  if (!broker) return null;

  const subAgents = allBrokers.filter(
    (b) => b.ParentBrokerID === broker.BrokerID && b.BrokerID !== broker.BrokerID
  );

  return (
    <div className="bkd">
      {/* Back link */}
      <div className="bkd__breadcrumb">
        <button className="bkd__back" onClick={() => navigate('/brokers')}>
          &larr; Back to Brokers
        </button>
      </div>

      {/* Header */}
      <div className="bkd__header card">
        <div className="bkd__header-top">
          <div className="bkd__header-info">
            <h1 className="bkd__name">{broker.Name || 'Unknown Broker'}</h1>
            <div className="bkd__header-badges">
              <span
                className={`bkd__type-badge bkd__type-badge--${broker.Type || 'wholesale'}`}
              >
                {broker.Type || '—'}
              </span>
              {broker.MasterCode && (
                <span className="bkd__code-badge">{broker.MasterCode}</span>
              )}
              <span
                className={`bkd__active-badge ${broker.Active !== false ? 'bkd__active-badge--active' : 'bkd__active-badge--inactive'}`}
              >
                {broker.Active !== false ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
          {!editing && (
            <button className="btn btn--secondary btn--sm" onClick={startEditing}>
              Edit
            </button>
          )}
        </div>
      </div>

      {/* Edit form */}
      {editing && (
        <div className="bkd__edit-card card">
          <h3 className="bkd__edit-title">Edit Broker Information</h3>
          <div className="bkd__edit-grid">
            <div className="bkd__edit-field">
              <label className="bkd__edit-label">Name</label>
              <input
                className="bkd__edit-input"
                value={editData.Name || ''}
                onChange={(e) => handleEditChange('Name', e.target.value)}
              />
            </div>
            <div className="bkd__edit-field">
              <label className="bkd__edit-label">Type</label>
              <select
                className="bkd__edit-input"
                value={editData.Type || 'wholesale'}
                onChange={(e) => handleEditChange('Type', e.target.value)}
              >
                <option value="wholesale">Wholesale</option>
                <option value="retail">Retail</option>
                <option value="aggregator">Aggregator/Network</option>
              </select>
            </div>
            <div className="bkd__edit-field">
              <label className="bkd__edit-label">Contact Email</label>
              <input
                className="bkd__edit-input"
                type="email"
                value={editData.ContactEmail || ''}
                onChange={(e) => handleEditChange('ContactEmail', e.target.value)}
              />
            </div>
            <div className="bkd__edit-field">
              <label className="bkd__edit-label">Master Code</label>
              <input
                className="bkd__edit-input"
                value={editData.MasterCode || ''}
                onChange={(e) => handleEditChange('MasterCode', e.target.value)}
              />
            </div>
            <div className="bkd__edit-field">
              <label className="bkd__edit-label">Phone</label>
              <input
                className="bkd__edit-input"
                value={editData.Phone || ''}
                onChange={(e) => handleEditChange('Phone', e.target.value)}
              />
            </div>
            <div className="bkd__edit-field">
              <label className="bkd__edit-label">Website</label>
              <input
                className="bkd__edit-input"
                value={editData.Website || ''}
                onChange={(e) => handleEditChange('Website', e.target.value)}
              />
            </div>
            <div className="bkd__edit-field">
              <label className="bkd__edit-label">Address</label>
              <input
                className="bkd__edit-input"
                value={editData.Address || ''}
                onChange={(e) => handleEditChange('Address', e.target.value)}
              />
            </div>
            <div className="bkd__edit-field">
              <label className="bkd__edit-label">City</label>
              <input
                className="bkd__edit-input"
                value={editData.City || ''}
                onChange={(e) => handleEditChange('City', e.target.value)}
              />
            </div>
            <div className="bkd__edit-field">
              <label className="bkd__edit-label">State</label>
              <input
                className="bkd__edit-input"
                maxLength={2}
                value={editData.State || ''}
                onChange={(e) =>
                  handleEditChange('State', e.target.value.toUpperCase())
                }
              />
            </div>
            <div className="bkd__edit-field">
              <label className="bkd__edit-label">Zip Code</label>
              <input
                className="bkd__edit-input"
                value={editData.ZipCode || ''}
                onChange={(e) => handleEditChange('ZipCode', e.target.value)}
              />
            </div>
            <div className="bkd__edit-field bkd__edit-field--full">
              <label className="bkd__edit-label">Notes</label>
              <textarea
                className="bkd__edit-input bkd__edit-textarea"
                value={editData.Notes || ''}
                onChange={(e) => handleEditChange('Notes', e.target.value)}
                rows={3}
              />
            </div>
          </div>
          {editError && <p className="bkd__edit-error">{editError}</p>}
          <div className="bkd__edit-actions">
            <button
              className="btn btn--secondary"
              onClick={() => setEditing(false)}
              disabled={editSaving}
            >
              Cancel
            </button>
            <button
              className="btn btn--primary"
              onClick={saveEdit}
              disabled={editSaving}
            >
              {editSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bkd__tabs">
        {TABS.map((tab) => (
          <button
            key={tab}
            className={`bkd__tab ${activeTab === tab ? 'bkd__tab--active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bkd__content">
        {/* ========= OVERVIEW ========= */}
        {activeTab === 'Overview' && (
          <div className="bkd__overview">
            {/* Company Info */}
            <div className="bkd__info-card card">
              <h3 className="bkd__section-title">Company Information</h3>
              <dl className="bkd__info-grid">
                <div className="bkd__info-item">
                  <dt>Address</dt>
                  <dd>
                    {[broker.Address, broker.City, broker.State, broker.ZipCode]
                      .filter(Boolean)
                      .join(', ') || '—'}
                  </dd>
                </div>
                <div className="bkd__info-item">
                  <dt>Phone</dt>
                  <dd>{broker.Phone || '—'}</dd>
                </div>
                <div className="bkd__info-item">
                  <dt>Website</dt>
                  <dd>
                    {broker.Website ? (
                      <a
                        href={
                          broker.Website.startsWith('http')
                            ? broker.Website
                            : `https://${broker.Website}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bkd__link"
                      >
                        {broker.Website}
                      </a>
                    ) : (
                      '—'
                    )}
                  </dd>
                </div>
                <div className="bkd__info-item">
                  <dt>Contact Email</dt>
                  <dd>{broker.ContactEmail || '—'}</dd>
                </div>
                <div className="bkd__info-item">
                  <dt>Master Code</dt>
                  <dd className="bkd__mono">{broker.MasterCode || '—'}</dd>
                </div>
                <div className="bkd__info-item">
                  <dt>Sub Code</dt>
                  <dd className="bkd__mono">{broker.SubCode || '—'}</dd>
                </div>
                <div className="bkd__info-item">
                  <dt>Created</dt>
                  <dd>{formatDate(broker.CreatedAt)}</dd>
                </div>
                <div className="bkd__info-item">
                  <dt>Last Updated</dt>
                  <dd>{formatDate(broker.UpdatedAt)}</dd>
                </div>
              </dl>
              {broker.Notes && (
                <div className="bkd__notes">
                  <dt>Notes</dt>
                  <dd>{broker.Notes}</dd>
                </div>
              )}
            </div>

            {/* Quick Stats — computed from real data */}
            <div className="bkd__stats-row">
              <div className="bkd__stat-card card">
                <span className="bkd__stat-label">Total Submissions</span>
                <span className="bkd__stat-value">{brokerSubmissions.length || '—'}</span>
              </div>
              <div className="bkd__stat-card card">
                <span className="bkd__stat-label">Bound Policies</span>
                <span className="bkd__stat-value bkd__stat-value--green">
                  {brokerSubmissions.filter(s => (s.Status || s.status) === 'bound').length || '—'}
                </span>
              </div>
              <div className="bkd__stat-card card">
                <span className="bkd__stat-label">Active Accounts</span>
                <span className="bkd__stat-value">{brokerAccounts.filter(a => (a.Status || a.status) === 'active').length || '—'}</span>
              </div>
            </div>

            {/* Sub-Agents / Offices */}
            <div className="bkd__sub-section card">
              <div className="bkd__sub-header">
                <h3 className="bkd__section-title">Offices / Teams</h3>
                <button
                  className="btn btn--secondary btn--sm"
                  onClick={() => setShowAddOffice(true)}
                >
                  + Add Office
                </button>
              </div>

              {showAddOffice && (
                <form className="bkd__office-form" onSubmit={handleAddOffice}>
                  <div className="bkd__office-form-grid">
                    <input
                      className="bkd__edit-input"
                      placeholder="Office name"
                      required
                      value={officeForm.Name}
                      onChange={(e) =>
                        setOfficeForm((f) => ({ ...f, Name: e.target.value }))
                      }
                    />
                    <input
                      className="bkd__edit-input"
                      placeholder="Sub code"
                      value={officeForm.SubCode}
                      onChange={(e) =>
                        setOfficeForm((f) => ({ ...f, SubCode: e.target.value }))
                      }
                    />
                    <input
                      className="bkd__edit-input"
                      placeholder="City"
                      value={officeForm.City}
                      onChange={(e) =>
                        setOfficeForm((f) => ({ ...f, City: e.target.value }))
                      }
                    />
                    <input
                      className="bkd__edit-input"
                      placeholder="State"
                      maxLength={2}
                      value={officeForm.State}
                      onChange={(e) =>
                        setOfficeForm((f) => ({
                          ...f,
                          State: e.target.value.toUpperCase(),
                        }))
                      }
                    />
                    <input
                      className="bkd__edit-input"
                      placeholder="Contact email"
                      type="email"
                      value={officeForm.ContactEmail}
                      onChange={(e) =>
                        setOfficeForm((f) => ({
                          ...f,
                          ContactEmail: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="bkd__office-form-actions">
                    <button
                      type="button"
                      className="btn btn--secondary btn--sm"
                      onClick={() => setShowAddOffice(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn--primary btn--sm"
                      disabled={officeSaving}
                    >
                      {officeSaving ? 'Adding...' : 'Add'}
                    </button>
                  </div>
                </form>
              )}

              {subAgents.length > 0 ? (
                <div className="bkd__sub-list">
                  {subAgents.map((sub) => (
                    <div
                      key={sub.BrokerID}
                      className="bkd__sub-item"
                      onClick={() => navigate(`/brokers/${sub.BrokerID}`)}
                    >
                      <div className="bkd__sub-name">{sub.Name}</div>
                      <div className="bkd__sub-meta">
                        {sub.SubCode && (
                          <span className="bkd__mono">{sub.SubCode}</span>
                        )}
                        <span>
                          {[sub.City, sub.State].filter(Boolean).join(', ') ||
                            '—'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="bkd__empty">No offices or teams found.</p>
              )}
            </div>
          </div>
        )}

        {/* ========= CONTACTS ========= */}
        {activeTab === 'Contacts' && (
          <div className="bkd__contacts">
            <div className="bkd__contacts-header">
              <h3 className="bkd__section-title">Contacts</h3>
              <button
                className="btn btn--secondary btn--sm"
                onClick={() => setShowAddContact(true)}
              >
                + Add Contact
              </button>
            </div>

            {showAddContact && (
              <form
                className="bkd__contact-form card"
                onSubmit={handleAddContact}
              >
                <div className="bkd__contact-form-grid">
                  <input
                    className="bkd__edit-input"
                    placeholder="Full name"
                    required
                    value={contactForm.name}
                    onChange={(e) =>
                      setContactForm((f) => ({ ...f, name: e.target.value }))
                    }
                  />
                  <input
                    className="bkd__edit-input"
                    placeholder="Email"
                    type="email"
                    value={contactForm.email}
                    onChange={(e) =>
                      setContactForm((f) => ({ ...f, email: e.target.value }))
                    }
                  />
                  <input
                    className="bkd__edit-input"
                    placeholder="Phone"
                    value={contactForm.phone}
                    onChange={(e) =>
                      setContactForm((f) => ({ ...f, phone: e.target.value }))
                    }
                  />
                  <input
                    className="bkd__edit-input"
                    placeholder="Title / Role"
                    value={contactForm.title}
                    onChange={(e) =>
                      setContactForm((f) => ({ ...f, title: e.target.value }))
                    }
                  />
                  <label className="bkd__checkbox-label">
                    <input
                      type="checkbox"
                      checked={contactForm.primary}
                      onChange={(e) =>
                        setContactForm((f) => ({
                          ...f,
                          primary: e.target.checked,
                        }))
                      }
                    />
                    Primary contact
                  </label>
                </div>
                <div className="bkd__contact-form-actions">
                  <button
                    type="button"
                    className="btn btn--secondary btn--sm"
                    onClick={() => setShowAddContact(false)}
                    disabled={contactSaving}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn--primary btn--sm" disabled={contactSaving}>
                    {contactSaving ? 'Adding...' : 'Add Contact'}
                  </button>
                </div>
              </form>
            )}

            {contactsLoading ? (
              <div className="bkd__tab-loading"><div className="spinner" /><p>Loading contacts...</p></div>
            ) : contacts.length > 0 ? (
              <div className="bkd__contact-list">
                {contacts.map((c) => {
                  const cId = c.ContactID || c.contactId || c.id;
                  const cName = c.Name || c.name || '';
                  const cEmail = c.Email || c.email || '';
                  const cPhone = c.Phone || c.phone || '';
                  const cTitle = c.Title || c.title || '';
                  const cPrimary = c.Primary ?? c.primary ?? false;
                  const isEditing = editingContactId === cId;

                  if (isEditing) {
                    return (
                      <form key={cId} className="bkd__contact-item bkd__contact-item--editing card" onSubmit={handleUpdateContact}>
                        <div className="bkd__contact-edit-grid">
                          <input className="bkd__edit-input" placeholder="Full name" required value={editContactForm.name} onChange={(e) => setEditContactForm(f => ({ ...f, name: e.target.value }))} />
                          <input className="bkd__edit-input" placeholder="Email" type="email" value={editContactForm.email} onChange={(e) => setEditContactForm(f => ({ ...f, email: e.target.value }))} />
                          <input className="bkd__edit-input" placeholder="Phone" value={editContactForm.phone} onChange={(e) => setEditContactForm(f => ({ ...f, phone: e.target.value }))} />
                          <input className="bkd__edit-input" placeholder="Title / Role" value={editContactForm.title} onChange={(e) => setEditContactForm(f => ({ ...f, title: e.target.value }))} />
                          <label className="bkd__checkbox-label">
                            <input type="checkbox" checked={editContactForm.primary} onChange={(e) => setEditContactForm(f => ({ ...f, primary: e.target.checked }))} />
                            Primary contact
                          </label>
                        </div>
                        <div className="bkd__contact-edit-actions">
                          <button type="button" className="btn btn--secondary btn--sm" onClick={() => setEditingContactId(null)} disabled={editContactSaving}>Cancel</button>
                          <button type="submit" className="btn btn--primary btn--sm" disabled={editContactSaving}>{editContactSaving ? 'Saving...' : 'Save'}</button>
                        </div>
                      </form>
                    );
                  }

                  return (
                    <div key={cId} className="bkd__contact-item card">
                      <div className="bkd__contact-top">
                        <span className="bkd__contact-name">{cName}</span>
                        <div className="bkd__contact-actions">
                          {cPrimary && <span className="bkd__primary-badge">Primary</span>}
                          <button className="bkd__contact-action-btn" title="Edit" onClick={() => startEditContact(c)}>&#9998;</button>
                          <button className="bkd__contact-action-btn bkd__contact-action-btn--delete" title="Delete" onClick={() => handleDeleteContact(cId)} disabled={deletingContactId === cId}>{deletingContactId === cId ? '...' : '\u{1F5D1}'}</button>
                        </div>
                      </div>
                      {cTitle && <span className="bkd__contact-title">{cTitle}</span>}
                      <div className="bkd__contact-details">
                        {cEmail && <span>{cEmail}</span>}
                        {cPhone && <span>{cPhone}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="bkd__empty">No contacts added yet.</p>
            )}
          </div>
        )}

        {/* ========= ACTIVITY ========= */}
        {activeTab === 'Activity' && (
          <div className="bkd__activity">
            <div className="bkd__activity-header">
              <h3 className="bkd__section-title">Activity Timeline</h3>
              <button
                className="btn btn--secondary btn--sm"
                onClick={() => setShowLogActivity(true)}
              >
                + Log Activity
              </button>
            </div>

            {showLogActivity && (
              <form
                className="bkd__activity-form card"
                onSubmit={handleLogActivity}
              >
                <div className="bkd__activity-form-grid">
                  <div className="bkd__edit-field">
                    <label className="bkd__edit-label">Type</label>
                    <select
                      className="bkd__edit-input"
                      value={activityForm.type}
                      onChange={(e) =>
                        setActivityForm((f) => ({
                          ...f,
                          type: e.target.value,
                        }))
                      }
                    >
                      {ACTIVITY_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="bkd__edit-field">
                    <label className="bkd__edit-label">Subject</label>
                    <input
                      className="bkd__edit-input"
                      required
                      value={activityForm.subject}
                      onChange={(e) =>
                        setActivityForm((f) => ({
                          ...f,
                          subject: e.target.value,
                        }))
                      }
                      placeholder="Brief subject"
                    />
                  </div>
                  <div className="bkd__edit-field bkd__edit-field--full">
                    <label className="bkd__edit-label">Description</label>
                    <textarea
                      className="bkd__edit-input bkd__edit-textarea"
                      value={activityForm.description}
                      onChange={(e) =>
                        setActivityForm((f) => ({
                          ...f,
                          description: e.target.value,
                        }))
                      }
                      rows={3}
                      placeholder="Details..."
                    />
                  </div>
                </div>
                <div className="bkd__activity-form-actions">
                  <button
                    type="button"
                    className="btn btn--secondary btn--sm"
                    onClick={() => setShowLogActivity(false)}
                    disabled={activitySaving}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn--primary btn--sm" disabled={activitySaving}>
                    {activitySaving ? 'Logging...' : 'Log Activity'}
                  </button>
                </div>
              </form>
            )}

            {activitiesLoading ? (
              <div className="bkd__tab-loading"><div className="spinner" /><p>Loading activities...</p></div>
            ) : activities.length > 0 ? (
              <div className="bkd__timeline">
                {activities.map((act, idx) => {
                  const actId = act.ActivityID || act.activityId || act.id || idx;
                  const actType = act.Type || act.type || 'Note';
                  const actSubject = act.Subject || act.subject || '';
                  const actDesc = act.Description || act.description || '';
                  const actDate = act.CreatedAt || act.createdAt || act.date || '';
                  const actUser = act.User || act.user || act.CreatedBy || act.createdBy || '';
                  return (
                    <div key={actId} className="bkd__timeline-item">
                      <div className="bkd__timeline-marker">
                        <div className="bkd__timeline-icon">
                          {ACTIVITY_ICONS[actType] || ACTIVITY_ICONS.Note}
                        </div>
                        {idx < activities.length - 1 && (
                          <div className="bkd__timeline-line" />
                        )}
                      </div>
                      <div className="bkd__timeline-content">
                        <div className="bkd__timeline-header">
                          <div>
                            <span className="bkd__timeline-type">{actType}</span>
                            <strong className="bkd__timeline-subject">
                              {actSubject}
                            </strong>
                          </div>
                          <span className="bkd__timeline-date">
                            {formatDateTime(actDate)}
                          </span>
                        </div>
                        {actDesc && (
                          <p className="bkd__timeline-desc">{actDesc}</p>
                        )}
                        <span className="bkd__timeline-user">{actUser}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="bkd__empty">No activity recorded yet.</p>
            )}
          </div>
        )}

        {/* ========= SUBMISSIONS & ACCOUNTS ========= */}
        {activeTab === 'Submissions & Accounts' && (
          <div className="bkd__subs-accts">
            {subsLoading ? (
              <div className="bkd__tab-loading">
                <div className="spinner" />
                <p>Loading submissions and accounts...</p>
              </div>
            ) : (
              <>
                {/* Submissions */}
                <div className="bkd__sa-section">
                  <h3 className="bkd__section-title">Submissions</h3>
                  {subsError ? (
                    <p className="bkd__sa-error">{subsError}</p>
                  ) : brokerSubmissions.length > 0 ? (
                    <div className="bkd__sa-table-wrap card">
                      <table className="bkd__sa-table">
                        <thead>
                          <tr>
                            <th>Insured</th>
                            <th>Status</th>
                            <th>Date</th>
                            <th>Premium</th>
                          </tr>
                        </thead>
                        <tbody>
                          {brokerSubmissions.map((sub) => (
                            <tr
                              key={sub.SubmissionID}
                              className="bkd__sa-row"
                              onClick={() =>
                                navigate(`/submissions/${sub.SubmissionID}`)
                              }
                            >
                              <td className="bkd__sa-name">
                                {sub.InsuredName || '—'}
                              </td>
                              <td>
                                <span
                                  className="bkd__sa-status"
                                  data-status={sub.Status}
                                >
                                  {(sub.Status || 'new').replace(/_/g, ' ')}
                                </span>
                              </td>
                              <td className="bkd__sa-date">
                                {formatDate(sub.CreatedAt)}
                              </td>
                              <td>
                                {sub.Premium ? formatCurrency(sub.Premium) : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="bkd__empty">No submissions found.</p>
                  )}
                </div>

                {/* Accounts */}
                <div className="bkd__sa-section">
                  <h3 className="bkd__section-title">Accounts</h3>
                  {acctsError ? (
                    <p className="bkd__sa-error">{acctsError}</p>
                  ) : brokerAccounts.length > 0 ? (
                    <div className="bkd__sa-table-wrap card">
                      <table className="bkd__sa-table">
                        <thead>
                          <tr>
                            <th>Insured</th>
                            <th>Status</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {brokerAccounts.map((acct) => (
                            <tr
                              key={acct.AccountID}
                              className="bkd__sa-row"
                              onClick={() =>
                                navigate(`/accounts/${acct.AccountID}`)
                              }
                            >
                              <td className="bkd__sa-name">
                                {acct.InsuredName || '—'}
                              </td>
                              <td>
                                <span
                                  className="bkd__sa-status"
                                  data-status={acct.Status}
                                >
                                  {(acct.Status || '—').replace(/_/g, ' ')}
                                </span>
                              </td>
                              <td className="bkd__sa-date">
                                {formatDate(acct.CreatedAt)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="bkd__empty">No accounts found.</p>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
